import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import type { MagicLinkVerifyResponse } from '@/api/types';
import { Sparkles, Loader2, XCircle, ArrowRight } from 'lucide-react';

export default function Verify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid or missing verification token.');
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await api.get<MagicLinkVerifyResponse>(
          `/auth/magic-link/verify?token=${token}`
        );

        if (response.success && response.data.token) {
          // login() now returns the fetched user (Phase 1E.1) so we
          // can branch on onboardingCompleted without a second hop
          // through context state.
          const fetchedUser = await login(response.data.token);
          setStatus('success');
          // Redirect chain:
          //   new user / onboarding not finished → /onboarding
          //   returning user                     → /  (unified dashboard)
          const target = fetchedUser?.onboardingCompleted ? '/' : '/onboarding';
          setTimeout(() => navigate(target), 1500);
        } else {
          throw new Error('Invalid response');
        }
      } catch (error) {
        setStatus('error');
        if (error instanceof Error) {
          setErrorMessage(error.message || 'Verification failed. The link may have expired.');
        } else {
          setErrorMessage('Verification failed. The link may have expired.');
        }
      }
    };

    verifyToken();
  }, [searchParams, login, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="card p-8 text-center">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
          </div>

          {status === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Verifying...</h2>
              <p className="text-gray-600">
                Please wait while we verify your magic link.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-twins-light rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-twins" />
              </div>
              <h2 className="text-xl font-semibold mb-2">You're in!</h2>
              <p className="text-gray-600 mb-4">
                Taking you to your Lab dashboard...
              </p>
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin mx-auto" />
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Verification Failed</h2>
              <p className="text-gray-600 mb-6">
                {errorMessage}
              </p>
              <Link
                to="/login"
                className="btn-primary inline-flex items-center gap-2"
              >
                Try Again
                <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
